import React, { useEffect, useState } from "react";
import { Button, Input, Textarea } from "../../../../design-system";
import {
  AREA_LABELS,
  BOARD_AREAS,
  BOARD_CARD_TYPES,
  BOARD_PRIORITIES,
  BoardArea,
  BoardCardType,
  BoardPriority,
} from "../../../../types/Board";
import { BoardCardInput } from "../../../../database/board/boardApi";
import { TYPE_LABELS } from "./IssueBoard";
import {
  ActionsRow,
  Backdrop,
  CloseBtn,
  ErrorText,
  FieldLabel,
  ModalBody,
  ModalCard,
  ModalFoot,
  ModalHead,
  ModalSub,
  ModalTitle,
  Select,
} from "./modalKit";

// Create-card form. Open to every admin; the creator identity is stamped by
// the caller (BoardScreen) from the logged-in storeUser.

const CardFormModal: React.FC<{
  onClose: () => void;
  onCreate: (input: BoardCardInput) => Promise<void>;
}> = ({ onClose, onCreate }) => {
  const [type, setType] = useState<BoardCardType>("bug");
  const [area, setArea] = useState<BoardArea>("customer_app");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<BoardPriority>("medium");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const submit = async () => {
    setError(null);
    if (!title.trim()) {
      setError("Give the card a title.");
      return;
    }
    setSaving(true);
    try {
      await onCreate({ type, area, title, description, priority });
      onClose();
    } catch (e: any) {
      setError(e?.message || "Could not create the card.");
      setSaving(false);
    }
  };

  return (
    <Backdrop onClick={() => !saving && onClose()}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <div>
            <ModalTitle>Add card</ModalTitle>
            <ModalSub>
              Report a bug, request a feature, or suggest an improvement.
            </ModalSub>
          </div>
          <CloseBtn aria-label="Close" onClick={() => !saving && onClose()}>
            ×
          </CloseBtn>
        </ModalHead>

        <ModalBody>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "var(--space-3)",
            }}
          >
            <div>
              <FieldLabel>Type</FieldLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value as BoardCardType)}
              >
                {BOARD_CARD_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <FieldLabel>Area</FieldLabel>
              <Select
                value={area}
                onChange={(e) => setArea(e.target.value as BoardArea)}
              >
                {BOARD_AREAS.map((a) => (
                  <option key={a} value={a}>
                    {AREA_LABELS[a]}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <FieldLabel>Priority</FieldLabel>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value as BoardPriority)}
              >
                {BOARD_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <FieldLabel>Title</FieldLabel>
            <Input
              value={title}
              placeholder="Short summary"
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <FieldLabel>Description</FieldLabel>
            <Textarea
              value={description}
              rows={5}
              placeholder="What happened, what you expected, or what you want."
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {error && <ErrorText>{error}</ErrorText>}
        </ModalBody>

        <ModalFoot>
          <ModalSub>Screenshots can be added after creating the card.</ModalSub>
          <ActionsRow>
            <Button variant="ghost" size="sm" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={saving}
              onClick={submit}
            >
              Create card
            </Button>
          </ActionsRow>
        </ModalFoot>
      </ModalCard>
    </Backdrop>
  );
};

export default CardFormModal;
